import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { gsap } from "gsap";
import ContainerCard from "./ContainerCard/ContainerCard";

/* =========================
   CONFIG
========================= */

const SLIDE_DURATION = 8;
const SCROLL_LOCK_TIME = 1200;

/* =========================
   STYLES
========================= */

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;

  .track {
    display: flex;
    will-change: transform;
  }

  .slide {
    width: 100%;
    flex-shrink: 0;
  }

  .header,
  .content {
    position: absolute;
    z-index: 1;
  }

  .header {
    top: 4rem;
    left: 2rem;
    right: 2rem;
    margin: 0 auto;
    widht: 100%;
  }

  .content {
    bottom: 0;
    left: 2rem;
    right: 2rem;
    max-height: 60%;
  }

  .card {
    position: relative;
    border-radius: 2rem;
    overflow: hidden;
    color: #fff;
    width: 93%;
    height: 54rem;
    box-shadow: 0 30px 70px rgba(0, 0, 0, 0.85);

    display: flex;
    align-items: flex-start;
    padding: 4rem 2rem;
  }

  @media only screen and (min-width: 992px) {
    .card {
      width: 98%;
      align-items: center;
      padding: 0 5rem;
    }

    .header {
      width: 40%;
      top: 10rem;
      left: 4.5rem;
      right: inherit;
      margin: 0;
    }

    .content {
      right: 3rem;
      width: 55%;
      max-height: 100%;
      left: inherit;
    }
  }
`;

const Controls = styled.div`
  gap: 1rem;
  margin-top: 5rem;

  .btn-control {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    background: rgb(91 91 91);
    border-radius: 3rem;
    height: 5rem;
    padding: 0 2rem;
  }
`;

const Dot = styled.button`
  position: relative;
  width: ${({ active }) => (active ? "4rem" : "1rem")};
  height: 0.9rem;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2rem;
  overflow: hidden;
  border: none;

  span {
    position: absolute;
    inset: 0;
    width: 0%;
    background: #fff;
  }
`;

const PlayButton = styled.button`
  background: rgb(91 91 91);
  color: #fff;
  border-radius: 50%;
  height: 5rem;
  width: 5rem;
  font-size: 2rem;
`;

/* =========================
   COMPONENT
========================= */

const HighlightCarousel = ({ items }) => {
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const barsRef = useRef([]);
  const progressTl = useRef(null);

  const scrollLockRef = useRef(false);
  const activeIndexRef = useRef(0);
  const isPlayingRef = useRef(true);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  /* =========================
     SYNC REFS
  ========================= */

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  /* =========================
     SLIDE MOVEMENT
  ========================= */

  const moveToSlide = (index) => {
    if (!wrapperRef.current || !trackRef.current) return;

    const width = wrapperRef.current.offsetWidth;

    gsap.to(trackRef.current, {
      x: -(index * width),
      duration: 1.1,
      ease: "power4.out",
      overwrite: "auto",
    });
  };

  /* =========================
     AUTOPLAY / PROGRESS
  ========================= */

  const startProgress = (index) => {
    if (progressTl.current) {
      progressTl.current.kill();
      progressTl.current = null;
    }

    if (!isPlayingRef.current) return;

    barsRef.current.forEach((bar, i) => {
      gsap.set(bar, { width: i < index ? "100%" : "0%" });
    });

    const tl = gsap.timeline({
      onComplete: () => {
        if (!isPlayingRef.current) return;

        const next = index + 1 < items.length ? index + 1 : 0;

        activeIndexRef.current = next;
        setActiveIndex(next);
        moveToSlide(next);
        startProgress(next);
      },
    });

    tl.to(barsRef.current[index], {
      width: "100%",
      duration: SLIDE_DURATION,
      ease: "none",
    });

    progressTl.current = tl;
  };

  /* =========================
     NAVIGATION
  ========================= */

  const goTo = (index) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
    moveToSlide(index);

    if (isPlayingRef.current) {
      startProgress(index);
    }
  };

  const togglePlay = () => {
    if (isPlayingRef.current) {
      isPlayingRef.current = false;
      setIsPlaying(false);
      progressTl.current?.kill();
      progressTl.current = null;
      return;
    }

    isPlayingRef.current = true;
    setIsPlaying(true);
    startProgress(activeIndexRef.current);
  };

  /* =========================
     SCROLL HANDLER
  ========================= */

  const handleWheel = (e) => {
    if (!items?.length) return;
    if (scrollLockRef.current) return;

    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;

    e.preventDefault();
    scrollLockRef.current = true;

    isPlayingRef.current = false;
    setIsPlaying(false);
    progressTl.current?.kill();
    progressTl.current = null;

    const direction = e.deltaX > 0 ? 1 : -1;
    const current = activeIndexRef.current;
    const next = (current + direction + items.length) % items.length;

    goTo(next);

    setTimeout(() => {
      scrollLockRef.current = false;
    }, SCROLL_LOCK_TIME);
  };

  /* =========================
     INIT
  ========================= */

  useEffect(() => {
    if (!items?.length) return;

    activeIndexRef.current = 0;
    isPlayingRef.current = true;

    setActiveIndex(0);
    setIsPlaying(true);

    moveToSlide(0);
    startProgress(0);

    return () => {
      progressTl.current?.kill();
    };
    // eslint-disable-next-line
  }, [items]);

  /* =========================
     LISTENER
  ========================= */

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    wrapper.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      wrapper.removeEventListener("wheel", handleWheel);
    };
    // eslint-disable-next-line
  }, []);

  /* =========================
     RENDER
  ========================= */

  return (
    <>
      <CarouselWrapper ref={wrapperRef}>
        <div className="track" ref={trackRef}>
          {items.map((info) => (
            <ContainerCard key={info._id} options={{ objinfo: info }} />
          ))}
        </div>
      </CarouselWrapper>

      <Controls className="t-flex-op4">
        <div className="btn-control">
          {items.map((item, i) => (
            <Dot
              key={item._id}
              active={i === activeIndex}
              onClick={() => goTo(i)}
            >
              <span ref={(el) => (barsRef.current[i] = el)} />
            </Dot>
          ))}
        </div>

        <PlayButton onClick={togglePlay}>{isPlaying ? "❚❚" : "▶"}</PlayButton>
      </Controls>
    </>
  );
};

export default HighlightCarousel;
